/**
 * LiquidityWater.tsx — full-page background edition
 *
 * Sits behind the entire app via position:fixed, fills from the bottom up.
 * Gold shimmer + gold bubbles fire whenever pendingRewards increases.
 *
 * Drop into src/components/LiquidityWater.tsx
 * Wire into App.tsx — see bottom of this file for usage snippet.
 *
 * TUNING CONSTANTS:
 *   TARGET_GROWTH_LP  — LP gain that = 100% fill range (lower for micro-pools)
 *   MIN_LEVEL         — idle floor fill  (0.12 = 12% from bottom)
 *   MAX_LEVEL         — ceiling fill     (0.72 = 72% — don't drown content)
 *   APR_WAVE_SCALE    — how strongly APR speeds / amplifies waves
 *   GOLD_DECAY_MS     — how long gold shimmer lingers after a reward tick
 */

import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";

// ─── Tuning ───────────────────────────────────────────────────────────────────
const TARGET_GROWTH_LP        = 1.0;
const MIN_LEVEL               = 0.12;
const MAX_LEVEL               = 0.72;
const APR_WAVE_SCALE          = 0.014;
const BUBBLE_INTERVAL_MS      = 3500;
const REWARD_BUBBLE_THRESHOLD = 0.00001;
const RIPPLE_DURATION_MS      = 1800;
const COMPOUND_DIP            = 0.03;
const GOLD_DECAY_MS           = 2800;

// ─── Types ────────────────────────────────────────────────────────────────────
interface Bubble {
  id: number;
  x: number;
  size: number;
  duration: number;
  delay: number;
  drift: number;
  opacity: number;
  gold: boolean;
}

export interface LiquidityWaterProps {
  userLP: number;
  initialLP: number;
  pendingRewards: number;
  apr: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function computeWaterLevel(userLP: number, initialLP: number): number {
  if (userLP <= 0) return MIN_LEVEL;
  const growth = Math.max(0, userLP - initialLP);
  const norm   = Math.log(1 + growth) / Math.log(1 + TARGET_GROWTH_LP);
  return MIN_LEVEL + Math.min(1, norm) * (MAX_LEVEL - MIN_LEVEL);
}

function buildWavePath(amplitude: number, h = 90): string {
  const mid = h / 2, a = amplitude;
  return [
    `M0,${mid}`,
    `C150,${mid-a} 300,${mid+a} 450,${mid}`,
    `C600,${mid-a} 750,${mid+a} 900,${mid}`,
    `C1050,${mid-a} 1150,${mid+a} 1200,${mid}`,
    `L1200,${h} L0,${h} Z`,
  ].join(" ");
}

// ─── Keyframes ────────────────────────────────────────────────────────────────
const KEYFRAMES = `
@keyframes lw-wave {
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
}
@keyframes lw-bubble {
  0%   { transform: translateY(0) translateX(0) scale(1); opacity: 1; }
  60%  { transform: translateY(-130px) translateX(calc(var(--drift)*0.6)) scale(1.05); opacity: 0.65; }
  100% { transform: translateY(-280px) translateX(var(--drift)) scale(0.35); opacity: 0; }
}
@keyframes lw-bubble-gold {
  0%   { transform: translateY(0) translateX(0) scale(1); opacity: 1; }
  35%  { transform: translateY(-90px) translateX(calc(var(--drift)*0.4)) scale(1.25); opacity: 0.9; }
  100% { transform: translateY(-320px) translateX(var(--drift)) scale(0.25); opacity: 0; }
}
@keyframes lw-ripple {
  0%   { opacity: 0.55; transform: scale(0.96); }
  50%  { opacity: 0.18; transform: scale(1.03); }
  100% { opacity: 0;    transform: scale(1); }
}
@keyframes lw-glow-pulse {
  0%, 100% { opacity: 0.3;  transform: scale(1); }
  50%       { opacity: 0.55; transform: scale(1.1); }
}
@keyframes lw-gold-scan {
  0%   { transform: translateX(-120%) skewX(-14deg); opacity: 0; }
  10%  { opacity: 1; }
  90%  { opacity: 0.7; }
  100% { transform: translateX(320%) skewX(-14deg); opacity: 0; }
}
`;

// ─── Component ────────────────────────────────────────────────────────────────
let _bid = 0;
function mkBubble(gold = false): Bubble {
  return {
    id: ++_bid,
    x:        3  + Math.random() * 93,
    size:     gold ? 7 + Math.random() * 11 : 3 + Math.random() * 9,
    duration: gold ? 4 + Math.random() * 3  : 5 + Math.random() * 5,
    delay:    Math.random() * 1.2,
    drift:    (Math.random() - 0.5) * 90,
    opacity:  gold ? 0.8 + Math.random() * 0.2 : 0.22 + Math.random() * 0.42,
    gold,
  };
}

export const LiquidityWater: React.FC<LiquidityWaterProps> = ({
  userLP, initialLP, pendingRewards, apr,
}) => {
  const targetLevel = useMemo(
    () => computeWaterLevel(userLP, initialLP),
    [userLP, initialLP]
  );

  // Smooth lerp
  const [displayLevel, setDisplayLevel] = useState(MIN_LEVEL);
  const dipRef    = useRef(false);
  const currentLv = useRef(MIN_LEVEL);

  useEffect(() => {
    let raf: number;
    const step = () => {
      const target = dipRef.current ? targetLevel - COMPOUND_DIP : targetLevel;
      currentLv.current += (target - currentLv.current) * 0.007;
      if (Math.abs(currentLv.current - target) < 0.0003) currentLv.current = target;
      setDisplayLevel(currentLv.current);
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [targetLevel]);

  // Bubbles
  const [bubbles, setBubbles] = useState<Bubble[]>([]);
  const addBubbles = useCallback((count: number, gold = false) => {
    const next = Array.from({ length: count }, () => mkBubble(gold));
    setBubbles(p => [...p, ...next]);
    const ttl = Math.max(...next.map(b => b.duration + b.delay)) * 1000 + 500;
    setTimeout(() => setBubbles(p => p.filter(b => !next.some(n => n.id === b.id))), ttl);
  }, []);

  // Ripple + gold
  const [rippling,    setRippling]    = useState(false);
  const [goldActive,  setGoldActive]  = useState(false);
  const goldTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevRew   = useRef(pendingRewards);

  useEffect(() => {
    const delta = pendingRewards - prevRew.current;
    if (delta > REWARD_BUBBLE_THRESHOLD) {
      const n = Math.min(14, Math.max(2, Math.floor(Math.log(1 + delta * 1000) * 2)));
      addBubbles(n, true);
      addBubbles(Math.ceil(n / 2), false);
      setGoldActive(true);
      if (goldTimer.current) clearTimeout(goldTimer.current);
      goldTimer.current = setTimeout(() => setGoldActive(false), GOLD_DECAY_MS);
    } else if (delta < -REWARD_BUBBLE_THRESHOLD && !dipRef.current) {
      dipRef.current = true;
      setRippling(true);
      addBubbles(6, true);
      setTimeout(() => { dipRef.current = false; }, RIPPLE_DURATION_MS);
      setTimeout(() => setRippling(false), RIPPLE_DURATION_MS);
    }
    prevRew.current = pendingRewards;
  }, [pendingRewards, addBubbles]);

  // Idle ambient bubbles
  useEffect(() => {
    const id = setInterval(
      () => addBubbles(1 + (Math.random() < 0.25 ? 1 : 0), false),
      BUBBLE_INTERVAL_MS
    );
    return () => clearInterval(id);
  }, [addBubbles]);

  const waveAmp       = Math.min(22, 5 + apr * APR_WAVE_SCALE * 10);
  const waveDur       = Math.max(5, 14 - apr * 0.06);
  const fillPct       = (1 - displayLevel) * 100;
  const bubbleBottomPct = displayLevel * 100 - 3;

  return (
    <>
      <style>{KEYFRAMES}</style>

      <div style={S.root} aria-hidden="true">

        {/* Static deep background */}
        <div style={S.bgGradient} />

        {/* Indigo ambient orbs */}
        <div style={{ ...S.orb, width:520, height:520, bottom:"2%",  left:"8%",  background:"rgba(79,70,229,0.11)",  animationDuration:"10s"               }} />
        <div style={{ ...S.orb, width:360, height:360, bottom:"18%", right:"6%", background:"rgba(109,40,217,0.09)", animationDuration:"13s", animationDelay:"-5s" }} />
        <div style={{ ...S.orb, width:260, height:260, bottom:"35%", left:"48%", background:"rgba(99,102,241,0.06)", animationDuration:"17s", animationDelay:"-8s" }} />

        {/* Gold reward orb — blooms near waterline on reward tick */}
        <div style={{
          ...S.orb,
          width:640, height:320,
          bottom: `${Math.max(0, bubbleBottomPct - 8)}%`,
          left: "15%",
          background: "rgba(251,191,36,0.08)",
          filter: "blur(65px)",
          animationDuration: "8s",
          opacity: goldActive ? 1 : 0,
          transition: "opacity 1s ease",
        }} />

        {/* ── Water body ──────────────────────────────────────────────────── */}
        <div style={{ ...S.water, top:`${fillPct.toFixed(2)}%` }}>

          {/* Gold scan sweep */}
          {goldActive && <div style={S.goldScan} />}

          {/* Wave 1 — primary indigo */}
          <div style={S.waveWrap}>
            <svg viewBox="0 0 1200 90" preserveAspectRatio="none"
              style={{ ...S.waveSvg, animationDuration:`${waveDur}s` }}>
              <path d={buildWavePath(waveAmp)} fill="rgba(79,70,229,0.32)" />
            </svg>
          </div>

          {/* Wave 2 — counter-drift violet */}
          <div style={{ ...S.waveWrap, top:"-10px" }}>
            <svg viewBox="0 0 1200 90" preserveAspectRatio="none"
              style={{ ...S.waveSvg, animationDuration:`${waveDur*1.5}s`, animationDirection:"reverse" }}>
              <path d={buildWavePath(waveAmp*0.6)} fill="rgba(139,92,246,0.18)" />
            </svg>
          </div>

          {/* Wave 3 — slow gold tint, visible only on reward */}
          <div style={{ ...S.waveWrap, top:"-5px", opacity: goldActive ? 1 : 0, transition:"opacity 1.2s ease" }}>
            <svg viewBox="0 0 1200 90" preserveAspectRatio="none"
              style={{ ...S.waveSvg, animationDuration:`${waveDur*2.3}s` }}>
              <path d={buildWavePath(waveAmp*0.38)} fill="rgba(251,191,36,0.13)" />
            </svg>
          </div>

          {rippling && <div style={S.ripple} />}
        </div>

        {/* ── Bubbles ─────────────────────────────────────────────────────── */}
        {bubbles.map(b => (
          <div key={b.id} style={{
            ...S.bubble,
            ...(b.gold ? S.bubbleGold : S.bubbleIndigo),
            left:   `${b.x}%`,
            bottom: `${Math.max(0.5, bubbleBottomPct)}%`,
            width:  b.size,
            height: b.size,
            opacity: b.opacity,
            animationName:     b.gold ? "lw-bubble-gold" : "lw-bubble",
            animationDuration: `${b.duration}s`,
            animationDelay:    `${b.delay}s`,
            "--drift":         `${b.drift}px`,
          } as React.CSSProperties} />
        ))}

        {/* Vignette — keeps edges dark so cards stay legible */}
        <div style={S.vignette} />

      </div>
    </>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const S: Record<string, React.CSSProperties> = {
  root: {
    position:      "fixed",
    inset:         0,
    zIndex:        0,
    pointerEvents: "none",
    overflow:      "hidden",
  },
  bgGradient: {
    position:   "absolute",
    inset:      0,
    background: "linear-gradient(180deg, #07071a 0%, #090918 55%, #0c0b22 100%)",
  },
  water: {
    position:   "absolute",
    left: 0, right: 0, bottom: 0,
    background: `linear-gradient(180deg,
      rgba(55,48,163,0.20) 0%,
      rgba(76,29,149,0.30) 45%,
      rgba(46,16,101,0.48) 100%
    )`,
  },
  waveWrap: {
    position:  "absolute",
    top: 0, left: 0,
    width:     "200%",
    height:    90,
    transform: "translateY(-74%)",
    overflow:  "hidden",
  },
  waveSvg: {
    position: "absolute", inset: 0,
    width: "100%", height: "100%",
    animationName:           "lw-wave",
    animationTimingFunction: "linear",
    animationIterationCount: "infinite",
  },
  goldScan: {
    position:               "absolute",
    inset:                  0,
    width:                  "35%",
    background:             "linear-gradient(90deg, transparent 0%, rgba(251,191,36,0.22) 50%, transparent 100%)",
    animationName:          "lw-gold-scan",
    animationDuration:      "2.4s",
    animationTimingFunction:"ease-in-out",
    animationIterationCount:"infinite",
    pointerEvents:          "none",
  },
  ripple: {
    position:   "absolute", inset: 0,
    background: "radial-gradient(ellipse at 50% 0%, rgba(167,139,250,0.25) 0%, transparent 65%)",
    animationName:           "lw-ripple",
    animationDuration:       `${RIPPLE_DURATION_MS}ms`,
    animationTimingFunction: "ease-out",
    animationFillMode:       "forwards",
  },
  bubble: {
    position:               "absolute",
    borderRadius:           "50%",
    animationTimingFunction:"ease-out",
    animationFillMode:      "forwards",
  },
  bubbleIndigo: {
    background: "radial-gradient(circle at 35% 35%, rgba(196,181,253,0.8), rgba(99,102,241,0.2))",
    border:     "1px solid rgba(167,139,250,0.3)",
    boxShadow:  "0 0 5px rgba(139,92,246,0.25)",
  },
  bubbleGold: {
    background: "radial-gradient(circle at 35% 30%, rgba(253,230,138,0.95), rgba(245,158,11,0.55))",
    border:     "1px solid rgba(251,191,36,0.65)",
    boxShadow:  "0 0 10px rgba(251,191,36,0.65), 0 0 22px rgba(245,158,11,0.3)",
  },
  orb: {
    position:               "absolute",
    borderRadius:           "50%",
    filter:                 "blur(52px)",
    animationName:          "lw-glow-pulse",
    animationTimingFunction:"ease-in-out",
    animationIterationCount:"infinite",
    pointerEvents:          "none",
  },
  vignette: {
    position:   "absolute", inset: 0,
    background: `
      radial-gradient(ellipse at 50% 0%,   transparent 35%, rgba(4,4,18,0.65) 100%),
      radial-gradient(ellipse at 0%   50%, rgba(4,4,18,0.35) 0%, transparent 55%),
      radial-gradient(ellipse at 100% 50%, rgba(4,4,18,0.35) 0%, transparent 55%)
    `,
    pointerEvents: "none",
  },
};

/*
 * ─── App.tsx wiring ──────────────────────────────────────────────────────────
 *
 * 1. Add an initialLp ref to capture baseline on first data load:
 *
 *   const initialLpRef = useRef<number | null>(null);
 *   useEffect(() => {
 *     if (initialLpRef.current === null && userTotalLp > 0) {
 *       initialLpRef.current = userTotalLp;
 *     }
 *   }, [userTotalLp]);
 *
 * 2. Compute avgApr from loaded vaults:
 *
 *   const aprVaults = vaults.filter(v => v.aprCompounded !== null);
 *   const avgApr = aprVaults.length
 *     ? aprVaults.reduce((s,v) => s + (v.aprCompounded ?? 0), 0) / aprVaults.length
 *     : 0;
 *
 * 3. Make sure the root div has position:relative and z-index:1 on its content:
 *
 *   // In s.root style object, add:  position: "relative"
 *   // In s.container style object, add: position: "relative", zIndex: 1
 *
 * 4. Render as the FIRST child of s.root, before the container div:
 *
 *   <div style={s.root}>
 *     <LiquidityWater
 *       userLP={userTotalLp}
 *       initialLP={initialLpRef.current ?? 0}
 *       pendingRewards={totalPending}
 *       apr={avgApr}
 *     />
 *     <div style={s.container}>
 *       ... rest of app ...
 *     </div>
 *   </div>
 */
